import { api } from '@agency/shared';
import { Callout, Card, KeyValue, PageHeader, Skeleton, fmtTimestamp } from '@agency/ui';
import { useEffect, useMemo, useState } from 'react';
import { ErrorCallout, Grid, JsonEditor, Page, SensitiveButton, UserLink } from '../../app/widgets.js';
import { useMutation } from '../../lib/mutate.js';
import { useSession } from '../../lib/session.js';
import { useApi } from '../../lib/useApi.js';

export function RateLimitsPage() {
  const session = useSession();
  const mutation = useMutation();
  const config = useApi('admin.ops.rateLimits.get');
  const history = useApi('admin.audit.query', { input: { action: 'ops.rate_limits_changed', limit: 20 } }, { enabled: session.has('audit.read') });
  const c = config.data;
  const canPublish = session.has('template.publish');
  const [text, setText] = useState('');
  useEffect(() => {
    if (c) setText(JSON.stringify({ providers: c.providers, retry: c.retry }, null, 2));
  }, [c]);
  const parsed = useMemo(() => {
    try {
      const v = api.adminOps.RateLimitConfigUpdateRequestSchema.omit({ revision: true }).safeParse(JSON.parse(text));
      return v.success ? { value: v.data, error: null } : { value: null, error: v.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; ') };
    } catch (e) {
      return { value: null, error: e instanceof Error ? e.message : 'invalid JSON' };
    }
  }, [text]);
  const dirty = c ? text !== JSON.stringify({ providers: c.providers, retry: c.retry }, null, 2) : false;
  const vanta = c?.providers.vanta;
  const drata = c?.providers.drata;
  return (
    <Page>
      <PageHeader
        eyebrow="System operations"
        eyebrowPath="Rate limits"
        title="Rate limits"
        subtitle={c ? `Version ${c.version} · Vanta ceiling ${vanta?.ceiling.requestsPerMinute ?? '--'}/min, OAuth ${vanta?.auth.requestsPerMinute ?? '--'}/min · Drata ceiling ${drata?.ceiling.requestsPerMinute ?? '--'}/min · egress group ${drata?.egressGroupCeilingPerMinute ?? '--'}/min · updated ${fmtTimestamp(c.updatedAt)}` : 'Loading configuration.'}
        actions={canPublish ? <SensitiveButton variant="primary" disabled={!dirty || !parsed.value} loading={mutation.busy} onClick={async () => { if (!c || !parsed.value) return; await mutation.run('admin.ops.rateLimits.set', { input: { ...parsed.value, revision: c.revision } }, { title: (d) => `Rate limits published · v${d.version}`, invalidate: ['admin.ops.rateLimits', 'admin.audit'] }); }}>Publish version</SensitiveButton> : undefined}
      />
      <ErrorCallout error={config.error} onRetry={() => void config.reload()} className="mt-6" />
      {!c ? (
        <Skeleton height={320} className="mt-6" />
      ) : (
        <Grid>
          <Card title="Active configuration" subtitle="Limits are configuration, not constants" span="col-span-12 lg:col-span-4">
            <KeyValue
              items={[
                { key: 'Version', value: String(c.version), mono: true },
                { key: 'Vanta ceiling', value: `${vanta?.ceiling.requestsPerMinute ?? '--'}/min · burst ${vanta?.ceiling.burst ?? '--'} · conc ${vanta?.ceiling.concurrency ?? '--'}`, mono: true },
                { key: 'Vanta OAuth', value: `${vanta?.auth.requestsPerMinute ?? '--'}/min`, mono: true },
                { key: 'Drata ceiling', value: `${drata?.ceiling.requestsPerMinute ?? '--'}/min · burst ${drata?.ceiling.burst ?? '--'} · conc ${drata?.ceiling.concurrency ?? '--'}`, mono: true },
                { key: 'Drata egress group', value: drata?.egressGroupCeilingPerMinute ? `${drata.egressGroupCeilingPerMinute}/min` : '--', mono: true },
                { key: 'Retries', value: `${c.retry.maxRetries} · base ${c.retry.baseDelayMs}ms · max ${c.retry.maxDelayMs}ms`, mono: true },
                { key: 'Timeouts', value: `connect ${c.retry.connectTimeoutMs}ms · response ${c.retry.responseTimeoutMs}ms · budget ${c.retry.perRequestBudgetMs}ms`, mono: true },
                { key: 'Updated by', value: <UserLink userId={c.updatedByUserId} /> },
                { key: 'Revision', value: String(c.revision), mono: true },
              ]}
            />
            <div className="mt-5 border-t border-hair pt-4">
              <p className="label-mono">History</p>
              {history.loading ? <Skeleton height={60} className="mt-2" /> : (history.data?.items.length ?? 0) === 0 ? <p className="mt-2 text-xs text-mute">No configuration changes recorded.</p> : (
                <ul className="mt-2 divide-y divide-hair text-[12.5px]">
                  {history.data?.items.map((e) => (
                    <li key={e.eventId} className="flex items-center justify-between py-1.5">
                      <span className="font-mono text-2xs text-mute">{fmtTimestamp(e.occurredAt)}</span>
                      <span className="flex items-center gap-2"><UserLink userId={e.actorUserId} />{e.details.policyVersion !== undefined ? <span className="font-mono text-2xs text-mute">v{e.details.policyVersion}</span> : null}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Card>
          <Card title="Edit" subtitle="Publishing creates a new version within validated bounds" span="col-span-12 lg:col-span-8">
            {!canPublish ? <Callout tone="info" className="mb-4">Publishing requires template.publish and recent authentication.</Callout> : null}
            <JsonEditor value={text} onChange={setText} rows={24} label="providers + retry (JSON)" error={parsed.error} />
          </Card>
        </Grid>
      )}
    </Page>
  );
}
